// <LineageExplorer> — FXC-8538 (Warp).
//
// Focus + expand navigation over a large backing graph. The backing
// graph can hold millions of atomic nodes (every output scalar — CL,
// CD, mesh, geometry); the explorer only ever hands the renderer the
// *revealed* neighbourhood, so the canvas never chokes and the view
// stays meaningful.
//
// Mirrors Thread's lineage UX (v1/v2 `LineageGraphView`): click a node
// to reveal its antecedents (upstream) and descendants (downstream).
// `onSelect` hands the host the clicked node plus its direct neighbours
// so it can render "antecedents / descendants" lists alongside.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  buildIndex,
  directAncestors,
  directDescendants,
  neighborhood,
  subgraph,
  type LineageDirection,
} from "../core/lineage";
import {
  atomicUnits,
  collapseClusters as collapseClustersTransform,
  communities,
  connectedComponents,
  maximalCliques,
  type Clustering,
} from "../core/cliques";
import { clusterColourFor } from "../core/palette";
import type { Graph, GraphEdge, GraphNode } from "../core/types";
import { ThreadGraph } from "./ThreadGraph";
import type { GraphControls, GraphGroup, GraphRenderer } from "./GraphRenderer";

const COLLAPSED_CLUSTER_ID_PREFIX = "cluster:";
const MAX_CLIQUE_CLUSTERING_NODES = 80;

/** How to cluster the revealed neighbourhood (or "none" to colour by type). */
export type ClusterMode = "none" | "communities" | "components" | "cliques" | "units";

/** Per-cluster summary for a legend. */
export interface ClusterInfo {
  id: number;
  color: string;
  size: number;
  /** Most common node type in the cluster (for a legend label). */
  dominantType: string;
}

export interface LineageSelection {
  node: GraphNode;
  /** Direct (1-hop) antecedents — what this node depends on. */
  antecedents: GraphNode[];
  /** Direct (1-hop) descendants — what depends on this node. */
  descendants: GraphNode[];
}

export interface LineageExplorerProps {
  /** Full backing graph. May be very large — only neighbourhoods render. */
  graph: Graph;
  /** Node to centre on initially. */
  focusId: string;
  /** Renderer name or component. Default "force". */
  renderer?: string | GraphRenderer;
  /** Hops revealed around the focus on mount / refocus. Default 1. */
  initialDepth?: number;
  /** Hops revealed each time a node is clicked. Default 1. */
  revealDepth?: number;
  /** Which way a click expands. Default "both". */
  expandDirection?: LineageDirection;
  /** Cluster the revealed neighbourhood and colour/outline by it. Default "none". */
  clusterBy?: ClusterMode;
  /** Scale node radius by degree. Default true (lineage hubs stand out). */
  sizeByDegree?: boolean;
  /** Collapse each cluster in view into a single super-node (structural overview). */
  collapse?: boolean;
  /** Animate flow particles along the selected node's lineage edges. Default true. */
  flowParticles?: boolean;
  /**
   * Node-type → colour, forwarded to the renderer (e.g. drive the graph off
   * the Thread V5 `--fc-*` tokens). When a cluster mode is active, cluster
   * colours take precedence per node; this drives the base colouring
   * otherwise. Omit for the built-in palette.
   */
  colourForType?: (type: string) => string;
  /** Edge-predicate → colour, forwarded to the renderer. Omit for the built-in palette. */
  colourForPredicate?: (predicate: string) => string;
  /**
   * Per-node colour override, forwarded to the renderer. An active cluster
   * colour still wins where present; this applies to the remaining nodes
   * (and to every node when `clusterBy="none"`). Omit for type/cluster colouring.
   */
  nodeColor?: (node: GraphNode) => string | undefined;
  /**
   * Search hits to reveal + highlight + focus. The host computes these
   * however it likes (semantic /api/search, FTS, label match) — Warp just
   * surfaces them in the graph. Changing the set reveals the hits + their
   * 1-hop neighbours and centres on the first present hit.
   */
  searchHits?: ReadonlySet<string>;
  /** Called whenever the selected node changes (incl. initial focus). */
  onSelect?: (selection: LineageSelection) => void;
  /** Called with the cluster legend and actual mode whenever clustering recomputes. */
  onClusters?: (clusters: ClusterInfo[], mode: ClusterMode) => void;
  /** Called once the renderer's imperative controls are available. */
  onReady?: (controls: GraphControls | null) => void;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  className?: string;
}

export function LineageExplorer({
  graph,
  focusId,
  renderer = "force",
  initialDepth = 1,
  revealDepth = 1,
  expandDirection = "both",
  clusterBy = "none",
  sizeByDegree = true,
  collapse = false,
  flowParticles = true,
  colourForType,
  colourForPredicate,
  nodeColor,
  searchHits,
  onSelect,
  onClusters,
  onReady,
  width,
  height,
  style,
  className,
}: LineageExplorerProps) {
  const lineageKey = useMemo(() => lineageGraphKey(graph), [graph.nodes, graph.edges]);
  const index = useMemo(() => buildIndex(graph), [graph.nodes, graph.edges]);
  const hasFocus = index.nodeById.has(focusId);

  const [revealed, setRevealed] = useState<Set<string>>(
    () => neighborhood(index, focusId, { depth: initialDepth }).ids,
  );
  const [selected, setSelected] = useState<string>(focusId);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const lastSelectionKeyRef = useRef<string | null>(null);
  const lastFocusResetRef = useRef<{
    lineageKey: string;
    focusId: string;
    initialDepth: number;
  } | null>(null);

  const emitSelect = useCallback(
    (id: string) => {
      const node = index.nodeById.get(id);
      const cb = onSelectRef.current;
      if (!node || !cb) return;
      const antecedents = directAncestors(index, id);
      const descendants = directDescendants(index, id);
      const key = selectionKey(node, antecedents, descendants);
      if (lastSelectionKeyRef.current === key) return;
      lastSelectionKeyRef.current = key;
      cb({ node, antecedents, descendants });
    },
    [index],
  );

  // Re-centre when the focus prop or backing graph content changes.
  useEffect(() => {
    const last = lastFocusResetRef.current;
    if (
      last?.lineageKey === lineageKey &&
      last.focusId === focusId &&
      last.initialDepth === initialDepth
    ) return;
    lastFocusResetRef.current = { lineageKey, focusId, initialDepth };
    setRevealed(neighborhood(index, focusId, { depth: initialDepth }).ids);
    setSelected(focusId);
    emitSelect(focusId);
  }, [index, lineageKey, focusId, initialDepth, emitSelect]);

  // Search hits → reveal them (+ 1-hop neighbours), highlight, and centre
  // on the first present hit. Host computes the hits (semantic/FTS/label).
  useEffect(() => {
    if (!searchHits || searchHits.size === 0) return;
    const present = [...searchHits].filter((id) => index.nodeById.has(id));
    if (present.length === 0) return;
    setRevealed((prev) => {
      const next = new Set(prev);
      for (const id of present)
        for (const r of neighborhood(index, id, { depth: 1 }).ids) next.add(r);
      return next;
    });
    setSelected(present[0]!);
    emitSelect(present[0]!);
  }, [searchHits, index, emitSelect]);

  const visible = useMemo(() => subgraph(index, revealed), [index, revealed]);
  const effectiveClusterBy: ClusterMode = clusterBy === "cliques" && visible.nodes.length > MAX_CLIQUE_CLUSTERING_NODES
    ? "communities"
    : clusterBy;

  // Cluster the *visible* slice (bounded — never the whole backing graph).
  const clustering = useMemo<Clustering | null>(() => {
    switch (effectiveClusterBy) {
      case "communities": return communities(visible);
      case "components": return connectedComponents(visible);
      case "units": return atomicUnits(visible);
      case "cliques": return cliqueClustering(visible);
      default: return null;
    }
  }, [visible, effectiveClusterBy]);

  const clusterNodeColor = useMemo(() => {
    if (!clustering) return undefined;
    return (node: GraphNode): string | undefined => {
      const c = clustering.labelOf.get(node.id);
      return c === undefined ? undefined : clusterColourFor(c);
    };
  }, [clustering]);

  const groups = useMemo<GraphGroup[]>(() => {
    if (!clustering) return [];
    const out: GraphGroup[] = [];
    for (const [cid, members] of clustering.clusters) {
      if (members.length < 2) continue; // skip singletons — no hull for a lone node
      out.push({ id: cid, color: clusterColourFor(cid), nodeIds: members });
    }
    return out;
  }, [clustering]);

  // Collapsed view: each cluster becomes one super-node (no hulls then).
  const rendered = useMemo(
    () => (collapse && clustering
      ? collapseClustersTransform(visible, clustering, { idPrefix: COLLAPSED_CLUSTER_ID_PREFIX })
      : visible),
    [collapse, clustering, visible],
  );
  const collapsedLookup = useMemo(
    () => (collapse && clustering ? collapsedClusterLookup(clustering) : null),
    [collapse, clustering],
  );
  const effectiveGroups = collapse ? [] : groups;
  // Compose colour sources: a collapsed view takes no per-node override; otherwise an
  // active cluster colour wins per node, falling back to the caller's nodeColor (which
  // applies to every node when no cluster mode is active). colourForType/Predicate are
  // forwarded straight through for the renderer's base colouring.
  const effectiveNodeColor = useMemo(() => {
    if (collapse) return undefined;
    if (clusterNodeColor && nodeColor) {
      return (node: GraphNode): string | undefined => clusterNodeColor(node) ?? nodeColor(node);
    }
    return clusterNodeColor ?? nodeColor;
  }, [collapse, clusterNodeColor, nodeColor]);

  // Click → reveal this node's antecedents/descendants and select it.
  const handleNodeClick = useCallback(
    (node: GraphNode) => {
      const backingIds = backingIdsForRenderedNode(node, collapsedLookup);
      const selectedId = backingIds.includes(selected)
        ? selected
        : (backingIds[0] ?? node.id);
      setRevealed((prev) => {
        const next = new Set(prev);
        for (const id of backingIds) {
          for (const revealedId of neighborhood(index, id, {
            depth: revealDepth,
            direction: expandDirection,
          }).ids)
            next.add(revealedId);
        }
        return next;
      });
      setSelected(selectedId);
      emitSelect(selectedId);
    },
    [index, emitSelect, revealDepth, expandDirection, collapsedLookup, selected],
  );

  // Right-click → re-centre the explorer on this node (reset the reveal).
  const handleNodeRecenter = useCallback(
    (node: GraphNode) => {
      const backingIds = backingIdsForRenderedNode(node, collapsedLookup);
      const selectedId = backingIds.includes(selected)
        ? selected
        : (backingIds[0] ?? node.id);
      setRevealed(neighborhood(index, selectedId, { depth: initialDepth }).ids);
      setSelected(selectedId);
      emitSelect(selectedId);
    },
    [index, initialDepth, emitSelect, collapsedLookup, selected],
  );

  // Emit the cluster legend when clustering recomputes.
  const onClustersRef = useRef(onClusters);
  onClustersRef.current = onClusters;
  useEffect(() => {
    const cb = onClustersRef.current;
    if (!cb) return;
    if (!clustering) {
      cb([], effectiveClusterBy);
      return;
    }
    const info: ClusterInfo[] = [...clustering.clusters.entries()]
      .map(([id, members]) => ({
        id,
        color: clusterColourFor(id),
        size: members.length,
        dominantType: dominantType(members, index.nodeById),
      }))
      .sort((a, b) => b.size - a.size);
    cb(info, effectiveClusterBy);
  }, [clustering, effectiveClusterBy, index]);

  // Highlight the selected node + its direct neighbours (the
  // antecedent/descendant frontier) and any search hits present in view.
  const highlightIds = useMemo(() => {
    const ids = new Set<string>([selected]);
    for (const n of directAncestors(index, selected)) ids.add(n.id);
    for (const n of directDescendants(index, selected)) ids.add(n.id);
    if (searchHits) for (const id of searchHits) if (index.nodeById.has(id)) ids.add(id);
    return collapsedLookup
      ? new Set([...ids].map((id) => collapsedLookup.renderedIdByBackingId.get(id) ?? id))
      : ids;
  }, [index, selected, collapsedLookup, searchHits]);

  if (!hasFocus) {
    throw new Error(`LineageExplorer focusId "${focusId}" is not present in graph.nodes`);
  }

  return (
    <ThreadGraph
      renderer={renderer}
      nodes={rendered.nodes}
      edges={rendered.edges}
      highlightIds={highlightIds}
      searchActive
      colourForType={colourForType}
      colourForPredicate={colourForPredicate}
      nodeColor={effectiveNodeColor}
      groups={effectiveGroups}
      sizeByDegree={sizeByDegree}
      flowParticles={flowParticles}
      onNodeClick={handleNodeClick}
      onNodeRightClick={handleNodeRecenter}
      onReady={onReady}
      width={width}
      height={height}
      style={style}
      className={className}
    />
  );
}

/** The most common node type among `members` (ties → first seen). */
function dominantType(members: string[], nodeById: Map<string, GraphNode>): string {
  const counts = new Map<string, number>();
  let best = "";
  let bestCount = 0;
  for (const id of members) {
    const t = nodeById.get(id)?.type;
    if (!t) continue;
    const c = (counts.get(t) ?? 0) + 1;
    counts.set(t, c);
    if (c > bestCount) {
      best = t;
      bestCount = c;
    }
  }
  return best;
}

interface CollapsedLookup {
  renderedIdByBackingId: Map<string, string>;
  backingIdsByRenderedId: Map<string, string[]>;
}

function collapsedClusterLookup(clustering: Clustering): CollapsedLookup {
  const renderedIdByBackingId = new Map<string, string>();
  const backingIdsByRenderedId = new Map<string, string[]>();
  for (const [clusterId, members] of clustering.clusters) {
    const renderedId = `${COLLAPSED_CLUSTER_ID_PREFIX}${clusterId}`;
    backingIdsByRenderedId.set(renderedId, members);
    for (const member of members) renderedIdByBackingId.set(member, renderedId);
  }
  return { renderedIdByBackingId, backingIdsByRenderedId };
}

function backingIdsForRenderedNode(node: GraphNode, lookup: CollapsedLookup | null): string[] {
  return lookup?.backingIdsByRenderedId.get(node.id) ?? [node.id];
}

function selectionKey(
  node: GraphNode,
  antecedents: GraphNode[],
  descendants: GraphNode[],
): string {
  return JSON.stringify([
    nodeSummary(node),
    antecedents.map(nodeSummary),
    descendants.map(nodeSummary),
  ]);
}

function lineageGraphKey(graph: Graph): string {
  return JSON.stringify([
    graph.nodes.map(nodeSummary),
    graph.edges.map(edgeSummary),
  ]);
}

function nodeSummary(node: GraphNode): [id: string, type: string, label: string] {
  return [node.id, node.type, node.label];
}

function edgeSummary(edge: GraphEdge): [source: string, target: string, predicate: string] {
  return [edge.source, edge.target, edge.predicate];
}

/**
 * Turn (overlapping) maximal cliques into a partition for colouring:
 * each node joins the largest clique it belongs to; nodes in no clique
 * become singletons.
 */
function cliqueClustering(graph: Graph): Clustering {
  const cliques = maximalCliques(graph, { minSize: 3 });
  const labelOf = new Map<string, number>();
  const clusters = new Map<number, string[]>();
  let next = 0;
  for (const clique of cliques) {
    const fresh = clique.filter((id) => !labelOf.has(id));
    if (fresh.length === 0) continue;
    const id = next++;
    clusters.set(id, fresh);
    for (const m of fresh) labelOf.set(m, id);
  }
  for (const n of graph.nodes) {
    if (labelOf.has(n.id)) continue;
    const id = next++;
    labelOf.set(n.id, id);
    clusters.set(id, [n.id]);
  }
  return { labelOf, clusters, count: clusters.size };
}
